import { Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { Clipboard } from '@angular/cdk/clipboard';
import { Subject, Subscription, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActionMonitorService } from '../../../services/action-monitor.service';
import { formatRunLog } from '../../../services/action-monitor-log';
import {
  ActionMonitorFrame,
  ActionMonitorLoopPass,
  ActionMonitorRerunState,
  ActionMonitorRow,
  ActionMonitorRun,
  ActionMonitorStep,
  XACT_READER_STEP_FAILED,
  framedRows,
  xactPassLabel,
  xactPassesOf,
  xactReaderCaption
} from '../../../models/action-monitor.model';
import { XatBotDataService } from '@features/xatbot/services/xat-bot-data.service';
import { XatBotEventService } from '@features/xatbot/services/xat-bot-event-service';
import { SecondaryCursorService } from '../../../services/secondary-cursor.service';

/**
 * THE ACTION MONITOR'S RUN VIEW (XCT-E-12-05) — the panel's body, moved out of `ActionMonitorComponent` as it was:
 * the title row with the live count, the progress bar, the rows grouped by batch and the footer with Stop, Re-run
 * and Copy log.
 *
 * TWO HOSTS, ONE RUN. The Action Monitor's own panel renders it, and so does the floating Xact Playground while its
 * window is up (see `actionMonitorHostFor`). Both read the one run `ActionMonitorService` holds, so neither host
 * can show a run the other does not: the view subscribes to the service itself rather than being handed the run.
 *
 * The host's own window chrome — the panel's Minimise and Close, the float's dismiss — is projected into the title
 * row, because that is where a reader looks for it and only the host knows what it does.
 *
 * `embedded` is the float's: a lighter title row under the float's own navy bar, and rows that take the window's
 * height instead of the panel's fixed one.
 */
@Component({
  standalone: true,
  selector: 'app-action-monitor-run-view',
  imports: [CommonModule, DragDropModule],
  templateUrl: './action-monitor-run-view.component.html',
  styleUrls: ['./action-monitor-run-view.component.scss']
})
export class ActionMonitorRunViewComponent implements OnInit, OnDestroy {
  /** The floating Xact Playground's variant. */
  @Input() @HostBinding('class.am-run-view--embedded') embedded = false;

  /**
   * No title row (XAT-E-377-02): the host draws its own bar over the run — the run window's Execution | Chat tabs, which
   * carry the run's state and count, and its own window controls. The rest of the view is unchanged.
   */
  @Input() titleless = false;

  /**
   * No Re-run or Copy log (review, 2026-09-27): a chat's run is the chat's to run again, and its reader follows the
   * conversation, not a log to paste. The footer keeps the outcome, and Stop while the run is going.
   */
  @Input() runActionless = false;

  /**
   * THE READER'S VIEW (XAT-E-526-03) — a run started from chat, for whoever asked, staff included: one list numbered
   * 1…N, each step its status, its plain caption and its time, "This step didn't work." under a failed one, and a plain
   * footer. None of the developer's vocabulary — no action badge, `sN` or id tooltip, batch header, command, detail
   * lines, pass labels or engine message — and no Re-run or Copy log. Every other run keeps the view, and the DOM the
   * probes read, it had.
   */
  @Input() @HostBinding('class.am-run-view--reader') reader = false;

  /** The line under a failed step in the reader's view. */
  readonly readerStepFailed = XACT_READER_STEP_FAILED;

  run: ActionMonitorRun | null = null;

  /** Whole seconds since the run started, recomputed by the ticker while — and only while — it runs. */
  elapsedSeconds = 0;

  /** The Copy log button's brief acknowledgement — the only feedback a clipboard write can give. */
  copied = false;

  @ViewChild('stepList') stepList: ElementRef<HTMLElement> | undefined;

  private destroy$ = new Subject<void>();
  private ticker: Subscription | null = null;

  /**
   * FOLLOW THE RUN, UNTIL THE DEVELOPER READS SOMETHING ELSE. The list scrolls to the action in flight —
   * which is the point of watching a run — but a developer who has scrolled back to look at an earlier
   * row must not be yanked forward every time the next action starts.
   */
  private followRunning = true;
  private lastRunningKey: string | null = null;
  private copiedTimer: any = null;

  /**
   * THE FRAMES THE READER HAS FOLDED AWAY (XAT-E-291-06), by the `call` step's key. Expanded is the default:
   * a call is opened BECAUSE its steps are what the reader wants to see, and a frame that arrived folded
   * would hide the run's actual work behind a click.
   */
  private collapsedFrames = new Set<string>();

  constructor(
    private monitor: ActionMonitorService,
    private xatBotDataService: XatBotDataService,
    private xatBotEventService: XatBotEventService,
    private secondaryCursorService: SecondaryCursorService,
    private clipboard: Clipboard
  ) {}

  ngOnInit(): void {
    this.monitor.run$.pipe(takeUntil(this.destroy$)).subscribe(run => {
      this.run = run;
      this.elapsedSeconds = run ? Math.max(0, Math.floor((Date.now() - run.startedAt) / 1000)) : 0;
      if (run?.status === 'running') this.startTicker(); else this.stopTicker();
      this.followTheRunningRow(run);
    });
  }

  ngOnDestroy(): void {
    this.stopTicker();
    clearTimeout(this.copiedTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** ⤢ — the full result in the page's expand dialog. Wired to the Playground in XAT-E-253-03. */
  openFullResult(): void { this.monitor.requestExpand(); }

  /**
   * Shown only for a run that has somewhere to expand to — an Xact run whose Playground page is on
   * screen. Navigate away and ⤢ goes with it, because the dialog it opens is the page's own.
   */
  get canOpenFullResult(): boolean { return this.monitor.canExpand; }

  // ── Stop ───────────────────────────────────────────────────────────────────────────────────────

  /**
   * ONE BUTTON, TWO STOPS — the only branch on `source` in the component, because the two runs are
   * driven from different ends and a single "stop" would have to lie about one of them.
   */
  stop(): void {
    if (this.run?.source === 'xact') {
      this.stopXact();
      return;
    }

    this.stopWorkflow();
  }

  /**
   * AutoPilot's stop, unchanged from the float: tell the thread to stop responding, tell the chat its
   * Stop was pressed, and tear the run overlay down.
   */
  private stopWorkflow(): void {
    if (this.run?.source !== 'workflow' || !this.run.threadId) return;

    this.xatBotDataService.postStopRespondingCommand(this.run.threadId, null, true, () => {});
    // To the run's own thread (XAT-E-364-08): with two chats alive, only the one showing it stops its answer.
    this.xatBotEventService.emitStopRespondButtonClick({ threadId: this.run.threadId });
    if (this.secondaryCursorService.isRunOverlayActive()) {
      this.secondaryCursorService.requestStop();
    }
    this.secondaryCursorService.setRunOverlayActive(false);
    this.secondaryCursorService.reset();
    this.monitor.stopWorkflow();
  }

  /**
   * AN XACT RUN STOPS THROUGH THE ABORT PATH (XAT-E-253-03), and through nothing else: the request
   * reaches the browser-action loop, which abandons the rest of the current batch; the executor marks
   * the tail `not-run` and answers; and the monitor settles that answer as *Stopped at sK*.
   *
   * Nothing is marked here on the way. A run this component declared stopped would be this component
   * guessing where the loop actually got to — and the answer that lands a moment later is the one view
   * that knows.
   */
  private stopXact(): void {
    this.secondaryCursorService.requestStop();
  }

  // ── Re-run, and the log ────────────────────────────────────────────────────────────────────────

  /** May this run be repeated, and if not, what the tooltip says. */
  get rerunState(): ActionMonitorRerunState {
    return this.monitor.rerunState;
  }

  /** Offered once a run has settled — running it again while it is going is the one thing it cannot do. */
  get canOfferRerun(): boolean {
    return this.run?.source === 'xact' && !this.isRunning;
  }

  /** Re-run and Copy log are the developer's: never on a chat run's reader view, nor where the host asked for none. */
  get offersRunActions(): boolean {
    return !this.runActionless && !this.reader;
  }

  rerun(): void {
    this.monitor.rerunXact();
  }

  /**
   * THE RUN, AS TEXT, ON THE CLIPBOARD. This is what gets pasted into a ticket, so it is the whole run —
   * every row, its batch, what failed and what never ran — and not the sentence the footer shows.
   */
  copyLog(): void {
    if (!this.run) return;

    this.clipboard.copy(formatRunLog(this.run));

    this.copied = true;
    clearTimeout(this.copiedTimer);
    this.copiedTimer = setTimeout(() => (this.copied = false), 1600);
  }

  // ── What the template reads ────────────────────────────────────────────────────────────────────

  get isRunning(): boolean { return this.run?.status === 'running'; }

  get counter(): string { return `${this.run?.done ?? 0} / ${this.run?.total ?? 0}`; }

  /**
   * THE RUN'S STATE AS A WORD (XAT-E-265-03) — what the state dot has always said in colour.
   *
   * The dot is 7px and carries no shape, so the hue was all of it; COLLAPSED that is the whole of what the
   * pill says, since the label never changes and the counter is only a fraction. This is that state in
   * words: it names the dot, and it fills the pill's tooltip.
   *
   * `timed-out` reads as "timed out" rather than as the status token: the tooltip is a sentence to a reader,
   * not a value to a caller, and every other word here already is one.
   */
  /**
   * THE STEP STATUS AS A SENTENCE (XAT-E-265-03) — what the row's glyph is named.
   *
   * The status is a VALUE, not a label: `not-run` announced as written comes out "not dash run", and
   * `pending` says nothing about whether the run is still coming to it. The cell is the row's only
   * statement of how its step ended, so the name it carries is English.
   */
  stepStatusLabel(step: ActionMonitorStep): string {
    switch (step?.status) {
      case 'success':
        return 'succeeded';
      case 'failed':
        return 'failed';
      case 'running':
        return 'running';
      case 'stopped':
        return 'stopped';
      case 'not-run':
        return 'not run';
      case 'skipped':
        return 'skipped';
      default:
        return 'not started';
    }
  }

  /** The drawer's title in the floating Xact Playground: the run's state, as a word a reader would say. */
  get stateWord(): string {
    switch (this.run?.status) {
      case 'running':
        return 'Running';
      case 'completed':
        return 'Finished';
      case 'failed':
        return 'Failed';
      case 'stopped':
        return 'Stopped';
      case 'timed-out':
        return 'Timed out';
      default:
        return 'Run';
    }
  }

  /**
   * WHICH KIND OF FAILURE ENDED THE RUN (XCT-E-91-01) — shown beside the footer's status and set as its `data-failure-kind`.
   * Null unless the run failed and the server said which kind, so the attribute is absent rather than empty.
   */
  get failureKind(): string | null {
    return this.run?.status === 'failed' && this.run.failureKind ? this.run.failureKind : null;
  }

  /** The footer's sentence: the run's outcome, and while it runs, that it is working — in the reader's words for a reader. */
  get footerText(): string {
    if (this.run?.outcome) return this.run.outcome;
    if (!this.isRunning) return '';
    return this.reader ? 'Working…' : 'Running…';
  }

  get statusLabel(): string {
    switch (this.run?.status) {
      case 'running':
        return 'running';
      case 'completed':
        return 'completed';
      case 'failed':
        return 'failed';
      case 'stopped':
        return 'stopped';
      case 'timed-out':
        return 'timed out';
      default:
        return '';
    }
  }

  get progressPercent(): number {
    const run = this.run;
    if (!run || !run.total) return 0;
    if (run.status === 'completed') return 100;
    // The step in flight is worth a fraction of a step, so a one-step run does not read 0% throughout.
    const inFlight = run.steps.some(s => s.status === 'running') ? 0.4 : 0;
    return Math.min(100, Math.round(((run.done + inFlight) / run.total) * 100));
  }

  get elapsed(): string {
    const s = this.elapsedSeconds;
    if (s < 60) return `${s}s`;
    return `${Math.floor(s / 60)}m ${`${s % 60}`.padStart(2, '0')}s`;
  }

  formatDuration(ms?: number): string {
    if (!ms || ms <= 0) return '';
    if (ms < 1000) return `${ms}ms`;
    const s = ms / 1000;
    if (s < 60) return `${s.toFixed(1)}s`;
    return `${Math.floor(s / 60)}m ${(s % 60).toFixed(0)}s`;
  }

  /**
   * A reader row's time (XAT-E-526-05): always in seconds, so the column reads as one unit — `0.6s`, `2.1s`, and
   * `1m 05s` from a minute up; never `ms`, and never `0.0s` for a step that took any time at all.
   */
  readerDuration(ms?: number): string {
    if (!ms || ms <= 0) return '';
    const tenths = Math.max(1, Math.round(ms / 100));
    if (tenths < 600) return `${(tenths / 10).toFixed(1)}s`;
    const s = Math.round(ms / 1000);
    return `${Math.floor(s / 60)}m ${`${s % 60}`.padStart(2, '0')}s`;
  }

  /**
   * The badge. An Xact step names its own browser action; a workflow step carries the coarse
   * `BrowserAction` for every step, which says nothing — so for those the label is read out of the
   * step's own words, exactly as the float did.
   */
  badgeLabel(step: ActionMonitorStep): string {
    const type = (step.actionType || '').trim();

    // A background row's badge is the COMMAND it dispatched (XAT-E-255-01), kept in its own case: a
    // PascalCase command name lower-cased and then shouted by the badge's uppercase is unreadable.
    if (step.command && type) return type;

    if (type && type.toLowerCase() !== 'browseraction') return type.toLowerCase();

    const name = (step.description || '').toLowerCase();
    if (name.includes('navigate') || name.includes('navigation')) return 'navigate';
    if (name.includes('upload') || name.includes('file')) return 'upload';
    if (name.includes('type') || name.includes('input') || name.includes('fill') || name.includes('enter')) return 'type';
    return 'click';
  }

  badgeClass(step: ActionMonitorStep): string {
    if (step.command) return 'cmd';
    if (step.status === 'skipped') return 'skip';

    const label = this.badgeLabel(step);
    if (label.includes('navigate')) return 'nav';
    if (label.includes('upload')) return 'upload';
    if (label.includes('type') || label.includes('input')) return 'type';
    return 'click';
  }

  /**
   * THE ROWS, GROUPED BY BATCH (XAT-E-253-03). A script that does two things runs as two batches, and the
   * second acts on the screen the first produced — so reading a run that stopped means knowing which batch
   * stopped and which never started. Derived here rather than held, off the same `groupIndex` the code and
   * the answer both stamp on the row.
   */
  get groups(): ActionMonitorStepGroup[] {
    const groups: ActionMonitorStepGroup[] = [];

    for (const step of this.run?.steps ?? []) {
      const index = step.groupIndex ?? 1;
      let group = groups.find(g => g.index === index);
      if (!group) {
        group = { index, summary: step.groupSummary, steps: [] };
        groups.push(group);
      }

      // The first row that carries one names the batch: a `not-run` tail the answer never described
      // would otherwise leave the header blank.
      if (!group.summary && step.groupSummary) group.summary = step.groupSummary;
      group.steps.push(step);
    }

    return groups;
  }

  /** Worth a header only when there is more than one — a single batch's header repeats the title bar. */
  get hasGroupHeaders(): boolean {
    return this.groups.length > 1;
  }

  /** A reader's row: its plain caption, else "Working in the platform" — never the step's script-shaped description. */
  readerCaption(step: ActionMonitorStep): string {
    return xactReaderCaption(step);
  }

  /**
   * ONE BATCH'S ROWS, WITH EVERY CALL AS A FRAME (XAT-E-291-06). The `call` and `return` steps the compiler
   * splices around an inlined script become one collapsible group titled by the script and the caller's
   * binding, and the callee's steps render inside it. Derived per read, off the same list the flat render
   * used — a run with no call in it produces exactly the rows it did before.
   */
  rowsIn(group: ActionMonitorStepGroup): ActionMonitorRow[] {
    return framedRows(group.steps, this.collapsedFrames);
  }

  isFrameCollapsed(frame: ActionMonitorFrame): boolean {
    return this.collapsedFrames.has(frame.key);
  }

  toggleFrame(frame: ActionMonitorFrame): void {
    if (this.collapsedFrames.has(frame.key)) {
      this.collapsedFrames.delete(frame.key);
      return;
    }

    this.collapsedFrames.add(frame.key);
  }

  /** How many steps a frame is hiding when it is folded — the only thing a collapsed group can still say. */
  frameCount(frame: ActionMonitorFrame): string {
    const count = frame.steps.length;
    return `${count} step${count === 1 ? '' : 's'}`;
  }

  /** A row's indent: the panel is narrow, so a nested frame steps in by a little rather than by a tab. */
  indentOf(row: ActionMonitorRow): number {
    return row.depth * 12;
  }

  /**
   * WHAT THE ROW LEADS WITH. The expectation when the step had one — "expected the Add Scenario dialog to
   * open" — because that is what the developer is reading a failed run for. The engine's own message is
   * selector-shaped and says what did not happen, never what was meant to, so it goes underneath.
   */
  headline(step: ActionMonitorStep): string {
    return step.expectation || step.description || step.actionType || step.key || '';
  }

  /**
   * THE PASS A LOOP'S ROW IS (XCT-E-14-07): the trail keeps only the latest pass of a body, so each body step is one row,
   * labelled with the pass of every loop around it — outermost first — and a `forEach` row with its own.
   * Empty on a row outside every loop, which renders exactly as it did.
   */
  passesOf(step: ActionMonitorStep): ActionMonitorLoopPass[] {
    return xactPassesOf(step);
  }

  passLabel(loop: ActionMonitorLoopPass): string {
    return xactPassLabel(loop);
  }

  /** The label's tooltip: which loop it is, by its variable and step. */
  passTitle(loop: ActionMonitorLoopPass): string {
    const which = loop.variable ? `the loop over '${loop.variable}'` : 'the loop';
    return `${xactPassLabel(loop)} of ${which} (step ${loop.scriptStep})`;
  }

  /**
   * Dimmed: the run never reached it, or it was cut off — or, on a background run, the runtime sent nothing
   * for it (`skipped`, XAT-E-255-01). None of them is a failure of that step.
   */
  isDim(step: ActionMonitorStep): boolean {
    return step.status === 'pending' || step.status === 'not-run' || step.status === 'stopped' || step.status === 'skipped';
  }

  /** The developer scrolled: stop following the run until they are back at the bottom of the list. */
  onStepsScrolled(): void {
    const el = this.stepList?.nativeElement;
    if (!el) return;

    this.followRunning = el.scrollHeight - el.scrollTop - el.clientHeight < 32;
  }

  trackGroup = (_: number, group: ActionMonitorStepGroup) => group.index;

  /**
   * ONE TRACK FUNCTION FOR BOTH ROW KINDS (XAT-E-291-06) — it replaced `trackStep`, which had nothing left
   * to track once the step list became a row list: a frame header and a step are different rows and must
   * never be diffed into each other, so the kind is part of the identity.
   */
  trackRow = (_: number, row: ActionMonitorRow) =>
    row.kind === 'frame' ? `frame:${row.frame.key}` : `step:${row.step.key}`;

  /**
   * Scroll to the action in flight — and only when the row in flight has CHANGED, so a run that is
   * ticking its duration does not fight a developer who is reading. Deferred by a macrotask because the
   * row it scrolls to is rendered by the change detection pass this subscription triggers.
   */
  private followTheRunningRow(run: ActionMonitorRun | null): void {
    const runningKey = run?.steps.find(s => s.status === 'running')?.key ?? null;
    if (runningKey === this.lastRunningKey) return;

    this.lastRunningKey = runningKey;
    if (!runningKey || !this.followRunning) return;

    setTimeout(() => {
      const row = this.stepList?.nativeElement?.querySelector('.am-step--running') as HTMLElement | null;
      row?.scrollIntoView({ block: 'nearest' });
    });
  }

  private startTicker(): void {
    if (this.ticker) return;
    this.ticker = interval(1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.run) return;
        this.elapsedSeconds = Math.max(0, Math.floor((Date.now() - this.run.startedAt) / 1000));
      });
  }

  private stopTicker(): void {
    this.ticker?.unsubscribe();
    this.ticker = null;
  }
}

/** One batch's rows, headed by that batch's summary — presentation only, derived from the run. */
export interface ActionMonitorStepGroup {
  index: number;
  summary?: string;
  steps: ActionMonitorStep[];
}
